import { createContext, useContext, useState, useCallback, lazy, Suspense, type ReactNode } from "react";
import type { UnifiedInsight } from "@/hooks/useUnifiedInsights";

const InsightDetailDialog = lazy(() => import("@/components/advisor/InsightDetailDialog"));

interface InsightDialogContextType {
  openInsightDialog: (insight: UnifiedInsight) => void;
  closeInsightDialog: () => void;
}

const InsightDialogContext = createContext<InsightDialogContextType | null>(null);

export function InsightDialogProvider({ children }: { children: ReactNode }) {
  const [insight, setInsight] = useState<UnifiedInsight | null>(null);
  const [open, setOpen] = useState(false);

  const openInsightDialog = useCallback((i: UnifiedInsight) => {
    setInsight(i);
    setOpen(true);
  }, []);

  const closeInsightDialog = useCallback(() => {
    setOpen(false);
    setTimeout(() => setInsight(null), 200);
  }, []);

  return (
    <InsightDialogContext.Provider value={{ openInsightDialog, closeInsightDialog }}>
      {children}
      {(open || insight) && (
        <Suspense fallback={null}>
          <InsightDetailDialog
            insight={insight}
            open={open}
            onClose={closeInsightDialog}
          />
        </Suspense>
      )}
    </InsightDialogContext.Provider>
  );
}

export function useInsightDialog() {
  const ctx = useContext(InsightDialogContext);
  if (!ctx) throw new Error("useInsightDialog must be used within InsightDialogProvider");
  return ctx;
}
